import React, { useEffect, useState } from "react";
import styled from "styled-components";
import GA4Event from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import LifeAtModal from "@common/LifeAtModal/LifeAtModal";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import SettingsIcon from "@icons/GenericSettingsSlidersIcon.svg";
import { FontSize } from "@themes/Fonts";
import {
    APPEARANCE_SETTINGS_MENU_BUTTON_LABEL,
    APPEARANCE_SETTINGS_MODAL_HEADER_LABEL,
    DARK_MODE_LABEL,
    FOCUS_MODE_SECTION_HEADER_LABEL,
    FOCUS_MODE_TIMEOUT_MAX_MS,
    FOCUS_MODE_TIMEOUT_MIN_MS,
    HIDE_ELEMENTS_SETTING_IDLE_TIMEOUT_LABEL,
    HIDE_ELEMENTS_SETTING_LABEL,
    LIGHT_MODE_LABEL,
    THEME_SECTION_HEADER_LABEL,
} from "@user/components/UserSettings/AppearanceSettingsConstants";
import LightDarkModeButton from "../../../../app/LifeAtApp/OverlayButtons/AllOverlayButtons/DisplayToggleButtons/LightDarkModeButton";
import { updateFocusModeEnabledSetting, updateFocusModeIdleTimeout } from "../../service/UserSettingsService";

// Styled Components
const Section = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    height: auto;
    width: auto;
`;

const SectionHeader = styled.span`
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorSecondary};
    padding: 6px 0;
`;

const Setting = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    margin: 6px 0;
    width: auto;
`;

const SettingLabel = styled.span`
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorPrimary};
    width: 70%;
`;

const SettingValue = styled.span`
    align-self: center;
`;

const StyledNumberInput = styled.input`
    height: 30px;
    width: 80px;

    padding-left: 10px;
    border-radius: 4px;
    vertical-align: center;

    color: ${(props) => props.theme.textColorPrimary};
    background-color: ${(props) => props.theme.backgroundTransparent};
    border: 1px solid ${(props) => props.theme.borderPrimary};
`;

const AppearanceSettingsIcon = styled(SettingsIcon)`
    stroke: ${(props) => props.theme.iconColor};
`;

export default function AppearanceSettingsModal() {
    const {
        lightMode,
        focusModeEnabled,
        setFocusModeEnabled,
        focusModeIdleTime,
        setFocusModeIdleTime,
    }: AppearanceContextType = useAppearanceContext();

    const { user, userData }: UserContextType = useUserContext();

    const [isModalOpen, setIsModalOpen] = useState(false);
    // Control this input using a string, so users can delete all of the contents in the input.
    const [currFocusModeTimeSec, setCurrFocusModeTimeSec] = useState<string>((focusModeIdleTime / 1000).toString());

    useEffect(() => {
        if (currFocusModeTimeSec) {
            updateFocusModeIdleTime(parseInt(currFocusModeTimeSec) * 1000);
        }
    }, [currFocusModeTimeSec]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const toggleFocusModeEnabled = () => {
        if (focusModeEnabled) {
            // Action is to disable
            GA4Service.logEvent(GA4Event.DISABLE_FOCUS_MODE);
        } else {
            // Action is to enable
            GA4Service.logEvent(GA4Event.ENABLE_FOCUS_MODE);
        }

        const updatedSetting = !focusModeEnabled;
        setFocusModeEnabled(updatedSetting);
        if (user && userData) {
            updateFocusModeEnabledSetting(user.uid, updatedSetting);
        }
    };

    const handleFocusModeIdleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value !== "") {
            const timeSec = parseInt(e.target.value);
            const timeMs = timeSec * 1000;
            updateFocusModeIdleTime(timeMs);
        } else {
            // Don't set the state value currFocusModeTimeSec here because we want
            // to allow the user to delete the contents of the input field.
            setCurrFocusModeTimeSec("");
            setFocusModeIdleTime(FOCUS_MODE_TIMEOUT_MIN_MS);
        }
    };

    const updateFocusModeIdleTime = (timeMs: number) => {
        if (timeMs >= FOCUS_MODE_TIMEOUT_MIN_MS && timeMs <= FOCUS_MODE_TIMEOUT_MAX_MS) {
            setCurrFocusModeTimeSec((timeMs / 1000).toString());
            setFocusModeIdleTime(timeMs);
            if (user && userData) {
                updateFocusModeIdleTimeout(user.uid, timeMs);
            }
        } else {
            setCurrFocusModeTimeSec((FOCUS_MODE_TIMEOUT_MIN_MS / 1000).toString());
            setFocusModeIdleTime(FOCUS_MODE_TIMEOUT_MIN_MS);
        }
    };

    const themeSettings = (
        <Section key={`user-appearance-settings-theme-section`}>
            <SectionHeader>{THEME_SECTION_HEADER_LABEL}</SectionHeader>

            <Setting>
                <SettingLabel>{lightMode ? LIGHT_MODE_LABEL : DARK_MODE_LABEL}</SettingLabel>
                <SettingValue>
                    <LightDarkModeButton />
                </SettingValue>
            </Setting>
        </Section>
    );

    const focusModeSettings = (
        <Section key={`user-appearance-settings-focus-mode-section`}>
            <SectionHeader>{FOCUS_MODE_SECTION_HEADER_LABEL}</SectionHeader>

            <Setting>
                <SettingLabel>{HIDE_ELEMENTS_SETTING_LABEL}</SettingLabel>
                <SettingValue>
                    <LifeAtCheckbox checked={focusModeEnabled} handleToggle={toggleFocusModeEnabled} />
                </SettingValue>
            </Setting>

            <Setting>
                <SettingLabel>{HIDE_ELEMENTS_SETTING_IDLE_TIMEOUT_LABEL}</SettingLabel>
                <SettingValue>
                    <StyledNumberInput
                        type="text"
                        maxLength={3}
                        autoComplete={"off"}
                        disabled={!focusModeEnabled}
                        value={currFocusModeTimeSec} // show in minutes
                        onChange={handleFocusModeIdleTimeChange}
                    />
                </SettingValue>
            </Setting>
        </Section>
    );

    const modalSections = [themeSettings, focusModeSettings];

    return (
        <>
            <LifeAtButton
                isLink={false}
                text={APPEARANCE_SETTINGS_MENU_BUTTON_LABEL}
                icon={<AppearanceSettingsIcon />}
                handleClick={handleOpenModal}
            />

            <LifeAtModal
                title={APPEARANCE_SETTINGS_MODAL_HEADER_LABEL}
                isOpen={isModalOpen}
                closeModal={handleCloseModal}
                notifications={[]}
            >
                {modalSections}
            </LifeAtModal>
        </>
    );
}
